import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { clientDuplicatesService } from '../../../_services/clientDuplicates.service';
import { clientSyncService } from '../../../_services/clientSync.service';
import { resultLine, refusalCopy, plural } from './duplicateCopy';
import './Duplicates.css';

const VISIBLE_MS = 12000;

/**
 * Dark toast after a bulk merge (mockup screen 3): "6 duplicates merged" with
 * "Undo all", or the partial line "5 merged. 1 couldn't be merged: …".
 * Undo all reverses the batch(es) newest-first on the server.
 * A single merge from Merge mode passes `mergeId` instead: "Clients merged"
 * with Undo, which rolls back that one merge.
 */
export default function MergeResultToast({ batchIds, results, mergeId, onClose, onUndone }) {
    const [text, setText] = useState(mergeId ? 'Clients merged. Their bookings are on the kept record.' : resultLine(results || []));
    const [undoing, setUndoing] = useState(false);
    const [done, setDone] = useState(false);
    const timer = useRef(null);
    const anyMerged = !!mergeId || (results || []).some((r) => r.ok);
    const canUndo = !!mergeId || !!(batchIds && batchIds.length);

    const arm = (ms) => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => onClose && onClose(), ms);
    };

    useEffect(() => {
        arm(VISIBLE_MS);
        return () => clearTimeout(timer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const undoAll = async () => {
        if (undoing || done || !canUndo) return;
        setUndoing(true);
        clearTimeout(timer.current);
        const failed = [];
        if (mergeId) {
            try {
                const res = await clientSyncService.rollbackMerge(mergeId);
                const d = res && res.data;
                if (d && d.statusCode === 0) failed.push((d.error && d.error.code) || 'invalid');
            } catch (e) {
                failed.push('invalid');
            }
        }
        for (const batchId of mergeId ? [] : [...batchIds].reverse()) {
            try {
                const out = await clientDuplicatesService.undo({ batchId });
                out.results.filter((r) => !r.ok).forEach((r) => failed.push(r.code));
            } catch (e) {
                failed.push(e.code || 'invalid');
            }
        }
        setUndoing(false);
        setDone(true);
        setText(failed.length
            ? (mergeId ? refusalCopy(failed[0]).body : `Undone, except ${plural(failed.length, 'client')}. ${refusalCopy(failed[0]).body}`)
            : 'Merge undone. The records are back as they were.');
        if (onUndone) onUndone();
        arm(6000);
    };

    return ReactDOM.createPortal(
        <div className="dup-toast" role="status" aria-live="polite">
            <span className="dup-toast-text">{text}</span>
            {anyMerged && !done && canUndo && (
                <button type="button" className="dup-toast-btn" onClick={undoAll} disabled={undoing}>
                    {undoing ? 'Undoing…' : (mergeId ? 'Undo' : 'Undo all')}
                </button>
            )}
            <button type="button" className="dup-toast-close" aria-label="Dismiss" onClick={() => onClose && onClose()}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
            </button>
        </div>,
        document.body
    );
}
